import { connect } from 'react-redux';
import { isUpdateArticlesLinkCsmEnabled } from '@helpcenter/feature-flags';
import { fetchHelpCenterAction } from 'epics/model/help-center';
import { fetchArticlesAction } from 'epics/model/kb-article';
import type { RouteContext } from 'react-resource-router';
import type { State } from 'state';
import { fetchArticle, fetchCrossSiteArticle, fetchArticleSpaceKey } from 'state/actions/article';
import { fetchLinkedSpaces } from 'state/actions/linked-spaces';
import { fetchPortalAction } from 'state/actions/portal';
import type { Portal } from 'state/persisted/portal';
import { getKbHelpCenterLinkDomainURLs } from 'state/selectors/help-center';
import { getArticle, getArticles } from 'state/selectors/kb-article';
import { getLinkedSpaces } from 'state/selectors/linked-spaces';

import {
    canCreateRequest,
    getIsKbServer,
    getPortalLink,
    getPortalOrError,
    getKbLinkDomainURLs,
} from 'state/selectors/portal';
import { isLoggedIn } from 'state/selectors/user';
import { PaperHeader } from 'view/common/paper-header';
import { withAnalyticsEvents } from '@atlaskit/analytics-next';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { isCSMHelpCenter } from '@atlassian/help-center-common-util/advanced-help-center';
import { getEnv } from '@atlassian/help-center-common-util/env';
import { hash } from '@atlassian/help-center-common-util/location';
import type { ArticleProps } from './article';
import Article from './article';
import type { Article as ArticleType } from 'state/persisted/kb-article/types';
import type { PersistedError } from 'state/persisted/types';

type ExternalProps = Omit<RouteContext, 'route' | 'action'>;

const selector = (state: State, props: ExternalProps) => {
    let articleId: string | undefined;
    let categoryId: string | undefined;
    let portalId: string | undefined;
    let projectId: number | undefined;
    let article: ArticleType | PersistedError | undefined;
    let shouldFetchPortal = false;
    let shouldFetchCategoryArticles = false;
    let redirectTo;
    let portalLink: string | undefined;
    let showRequestCreateButton: boolean | undefined;
    let isKbServerInstance: boolean = false;
    let kbLinkDomainURLs: string[] = [];
    const isAnonymousAccount: boolean = !isLoggedIn(state);

    if ('pageId' in props.match.params) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        articleId = props.match.params.pageId!;
        article = getArticle(state, articleId);
        // redirect from /kb/view/:id to /article/:id
        // we should keep this 'pageId' in ... block as it provides backwards
        // compatibility with the legacy /kb/view.
        const baseUrl = `/article/${articleId}${hash()}`;
        const currentUrl = new URL(window.location.href);
        const queryParams = currentUrl.search;
        redirectTo = `${baseUrl}${queryParams}`;
    } else {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        articleId = props.match.params.articleId!;
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        categoryId = props.match.params.categoryId!;
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        portalId = props.match.params.portalId!;

        if (portalId) {
            isKbServerInstance = getIsKbServer(state, Number(portalId));
            kbLinkDomainURLs = getKbLinkDomainURLs(state, Number(portalId));
        } else {
            kbLinkDomainURLs = getKbHelpCenterLinkDomainURLs(state);
        }
        const portal = portalId ? getPortalOrError(state, Number(portalId)) : undefined;
        const categoryArticles = categoryId ? getArticles(state, categoryId) : undefined;
        if (portalId && portal && !('error' in portal)) {
            projectId = portal.projectId;
        }
        article = getArticle(state, articleId);

        shouldFetchPortal = !!(portalId && !portal);
        shouldFetchCategoryArticles =
            !!(projectId && portalId && portal && !('error' in portal) && !categoryArticles) && !isKbServerInstance;

        redirectTo = getArticleRedirect(portalId, portal, categoryId, categoryArticles, articleId, article);

        portalLink = getPortalLink(state, Number(portalId));
        showRequestCreateButton = canCreateRequest(state, Number(portalId));
    }

    if (isUpdateArticlesLinkCsmEnabled() && isCSMHelpCenter(getEnv().helpCenterType)) {
        redirectTo = undefined;
    }

    const articleProps =
        article && !('error' in article)
            ? {
                  title: article.title,
                  viewUrl: article.viewUrl,
                  space: article.space,
                  projects: article.projects,
                  hostname: article.hostname,
              }
            : {
                  title: undefined,
                  viewUrl: undefined,
                  space: undefined,
                  projects: undefined,
                  hostname: undefined,
              };

    const linkedSpaces = FeatureGates.checkGate('fetch-all-accesible-linked-spaces-for-user')
        ? getLinkedSpaces(state)
        : undefined;

    const connectedProps = {
        projectId,
        portalId,
        categoryId,
        articleId,
        portalLink,
        showRequestCreateButton,
        redirectTo,
        PaperHeader,
        isKbServerInstance,
        kbLinkDomainURLs,
        shouldFetchPortal,
        shouldFetchCategoryArticles,
        isAnonymousAccount,
        linkedSpaces,
        isLoading: !article,
        error: article && 'error' in article ? article.error : undefined,
        query: props.query,
        ...articleProps,
    };

    // assert we return reasonable state
    // the reason we use an assertion instead of setting the selector's return type is
    // we don't want to make this function return Partial <ArticleProps>, because that
    // will be too lenient on what data we give to the article component
    connectedProps as Omit<
        ArticleProps,
        | 'fetchPortal'
        | 'fetchCategoryArticles'
        | 'fetchArticle'
        | 'fetchHelpCenter'
        | 'fetchCrossSiteArticle'
        | 'fetchArticleSpaceKey'
        | 'fetchLinkedSpaces'
    >;

    return connectedProps;
};

function getArticleRedirect(
    portalId: string | undefined,
    portal: Portal | PersistedError | undefined,
    categoryId: string | undefined,
    categoryArticles: ArticleType[] | PersistedError | undefined,
    articleId: string,
    article: ArticleType | PersistedError | undefined
): string | undefined {
    if (!portalId) {
        // when the article is only in one portal that the user can view, redirect the user there
        // this is done as a migration step from /kb/view/:id to /portal/_/article/:id
        if (article && !('error' in article)) {
            if (article.projects && article.projects.length === 1) {
                const newPortalId = article.projects[0].portalId;
                const baseUrl = `/portal/${newPortalId}/article/${articleId}${hash()}`;
                const currentUrl = new URL(window.location.href);
                const queryParams = currentUrl.search;
                const redirectUrl = `${baseUrl}${queryParams}`;
                return redirectUrl;
            }
        }
        return undefined;
    }

    // we only redirect when we have portal data
    if (!portal || 'error' in portal) {
        return undefined;
    }

    // we only redirect when we have article data
    if (!article || 'error' in article) {
        return undefined;
    }

    // -- at this point we have valid portal and article data -- //

    // if the article is not in the current portal, we take the user out of the portal
    if (article.projects && !article.projects.some((project) => project.portalId === portal.id)) {
        return `/article/${articleId}${hash()}`;
    }

    // for when the URL has a category ID in it
    if (categoryId && categoryArticles) {
        if ('error' in categoryArticles) {
            // if the category doesn't exist, we remove it from the URL
            if (categoryArticles.error.status === 404) {
                return `/portal/${portalId}/article/${articleId}${hash()}`;
            }
        } else if (!categoryArticles.find((a) => a.id === articleId)) {
            // if the article is not in the category, we remove it from the URL
            return `/portal/${portalId}/article/${articleId}${hash()}`;
        }
    }

    return undefined;
}

export default connect(selector, {
    fetchArticle,
    fetchCrossSiteArticle,
    fetchArticleSpaceKey,
    fetchLinkedSpaces,
    fetchCategoryArticles: fetchArticlesAction,
    fetchPortal: fetchPortalAction,
    fetchHelpCenter: fetchHelpCenterAction,
})(withAnalyticsEvents()(Article));
